import { useApiCall } from "@/app/api/apiCall";
import { Flat, flatten, PostPayload } from "@/app/api/types";
import AppLayout from "@/app/components/AppLayout";
import DetailPageLayout from "@/app/components/DetailPageLayout";
import { Footer } from "@/app/components/Footer";
import { FormField } from "@/app/components/form-fields/FormField";
import {
  IPrivateContext,
  PrivateContext,
} from "@/app/components/PrivateContextLoader";
import { SubmitButton } from "@/app/components/SubmitButton";
import { useApiContext } from "@/app/contexts/ApiContext";
import { Entity, IField } from "@/app/fields/types";
import { useStandardToast } from "@/app/hooks/useStandardToast";
import { INavLinks, NavContext } from "@/app/NavContext";
import { cleanPayload } from "@/app/utils/cleanPayload";
import { Button, Flex } from "@chakra-ui/react";
import { BackButton, Page, PageBody, PageHeader } from "@saas-ui-pro/react";
import { DefaultValues, Form, LoadingSpinner } from "@saas-ui/react";
import { useContext, useEffect, useState } from "react";
import { useLocation, useParams } from "react-router";
import { useNavigate } from "react-router-dom";

export const EditPage = <T extends Entity>() => {
  const { getData, updateEntry, deleteEntry, getFieldOptions } =
    useApiContext();
  const { fields, user } = useContext(PrivateContext) as IPrivateContext<T>;
  const params = useParams();
  const id = params["id"] as string;
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const resourceNamePlural = pathname.split("/")[1];
  const resourceName = resourceNamePlural
    .slice(0, resourceNamePlural.length - 1)
    .replace("-", " ");
  const { navLinks } = useContext(NavContext);
  const href = navLinks[`/${resourceNamePlural}` as keyof INavLinks];
  const handle = useApiCall();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [data, setData] = useState<T | undefined>();
  const [isSaving, setIsSaving] = useState(false);

  const fetchData = async (id: string) => {
    await handle(
      () => getData(id),
      (response) => setData(response)
    );
  };

  useEffect(() => {
    fetchData(id).then(() => {
      setIsLoading(false);
    });
  }, []);

  const toast = useStandardToast();
  const onEdit = async (_payload: PostPayload<T>, fields: IField<T>[]) => {
    setIsSaving(true);
    const payload = cleanPayload(_payload, fields);
    await handle(
      () =>
        updateEntry({
          id,
          ...payload,
        }),
      () => {
        toast({
          title: `${resourceName.slice(0, 1).toUpperCase()}${resourceName.slice(
            1
          )} updated`,
          status: "success",
        });
        navigate(href);
      },
      () => {
        setIsSaving(false);
      }
    );
  };

  const onDelete = async () => {
    setIsSaving(true);
    await handle(
      () => deleteEntry!(id),
      () => {
        toast({
          title: `${resourceName.slice(0, 1).toUpperCase()}${resourceName.slice(
            1
          )} deleted`,
          status: "success",
        });
        navigate(href);
      },
      () => setIsSaving(false)
    );
  };

  // all users can edit their own entries; admin users can edit any entries
  // only admin users can delete
  const canEdit =
    !!user && !!data && (user.is_admin || user.id === data.created_by);
  const canDelete = !!user && user.is_admin;

  return (
    <AppLayout>
      {isLoading || !fields || !data || !user ? (
        <Flex
          flexDirection="column"
          width="100%"
          height="100%"
          justifyContent="center"
          alignItems="center"
        >
          <LoadingSpinner />
        </Flex>
      ) : (
        <Form
          onSubmit={(p: Flat<T>) => onEdit(p, fields)}
          className="contents"
          defaultValues={flatten(data) as DefaultValues<Flat<T>>}
        >
          <Page>
            <PageHeader
              title={`${canEdit ? "Edit" : "View"} ${resourceName}`}
              nav={<BackButton href={href} data-cy="page-back-button" />}
            />
            <PageBody>
              <DetailPageLayout<T>
                fields={fields}
                render={(field) => (
                  <FormField
                    isDisabled={!canEdit || isSaving}
                    field={field}
                    getFieldOptions={getFieldOptions}
                  />
                )}
              />
            </PageBody>
            <Footer>
              <div className="w-full flex justify-end">
                {!!deleteEntry && (
                  <Button
                    disabled={!canDelete || isSaving}
                    data-cy="delete-entry-button"
                    onClick={onDelete}
                    className="mr-4"
                  >
                    Delete
                  </Button>
                )}
                <SubmitButton isDisabled={!canEdit || isSaving}>
                  Save changes
                </SubmitButton>
              </div>
            </Footer>
          </Page>
        </Form>
      )}
    </AppLayout>
  );
};
